// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
import { DetailedAsset } from '@/asset/api/types';
import {
  getRemainingLifeMonths,
  getTooltipInitialCostBasis,
  getTooltipPeriodSubLabel,
  sanitizeFilePart,
} from '@/asset/components/depreciation-chart-utilities';
import { IsoCurrencyCode } from '@/currency/constants/iso';
import {
  buildCSVContent,
  escapeCSV,
  formatAmountForCSV,
  generateCSVFilename,
  triggerCSVDownload,
} from '@/utilities/csv-export';
import { DateTime } from 'luxon';

interface BuildDepreciationScheduleCSVContentProps {
  acquisitionCost: number | null;
  capitalizedCost: number | null;
  endOfUsefulLife: DateTime | null;
  exitDate: DateTime | null;
  inServiceDate: DateTime | null;
  /** Denomination of every amount in the file. */
  currencyCode: IsoCurrencyCode;
  periods: NonNullable<DetailedAsset['accounting']['depreciationPeriods']>;
  salvageValue: number | null;
  usefulLifeMonths: number | null;
}

interface ExportDepreciationScheduleCSVProps
  extends BuildDepreciationScheduleCSVContentProps {
  assetSerial: string;
}

export function buildDepreciationScheduleCSVContent(
  props: BuildDepreciationScheduleCSVContentProps
) {
  // Amounts are written bare, so the file needs to say what they are denominated in --
  // otherwise two exports from assets in different currencies are indistinguishable.
  const headers = [
    'Month',
    'Period',
    'Remaining life (months)',
    'Currency',
    'Cost basis',
    'Salvage value',
    'Depreciation in month',
    'Accumulated depreciation',
    'Net book value',
  ];
  const rows = props.periods.map((period) => [
    escapeCSV(period.date.toFormat('LLLL yyyy')),
    escapeCSV(
      getTooltipPeriodSubLabel({
        date: period.date,
        inServiceDate: props.inServiceDate,
        usefulLifeMonths: props.usefulLifeMonths,
      })
    ),
    String(
      getRemainingLifeMonths({
        date: period.date,
        endOfUsefulLife: props.endOfUsefulLife,
        exitDate: props.exitDate,
        inServiceDate: props.inServiceDate,
      }) ?? ''
    ),
    escapeCSV(props.currencyCode),
    formatAmountForCSV(
      getTooltipInitialCostBasis({
        acquisitionCost: props.acquisitionCost,
        capitalizedCost: props.capitalizedCost,
        inServiceDate: props.inServiceDate,
        tooltipDate: period.date,
      }).toFixed(2)
    ),
    formatAmountForCSV(props.salvageValue?.toFixed(2)),
    formatAmountForCSV(period.depreciation.amount.toFixed(2)),
    formatAmountForCSV(period.accumulated.amount.toFixed(2)),
    formatAmountForCSV(period.bookValue.amount.toFixed(2)),
  ]);

  return buildCSVContent(headers, rows);
}

export function exportDepreciationScheduleCSV(
  props: ExportDepreciationScheduleCSVProps
) {
  const csvContent = buildDepreciationScheduleCSVContent(props);
  const filename = generateCSVFilename(
    `depreciation-schedule-${sanitizeFilePart(props.assetSerial)}`
  );

  triggerCSVDownload(csvContent, filename);
}
